import client from "./client";
import { pollVistaJob } from "./vistaBatchExport";
import { JobStartResponse, JobStatus, JobStatusEnum } from "../interfaces/V2Interface";

/** One reconciliation problem on one employee's day, straight from the API's 422 payload. */
export interface SageReconciliationIssue {
    workDate: string;
    kind: string;
    poolKey: string | null;
    payCodeName: string | null;
    targetHours: number | null;
    actualHours: number | null;
    deltaHours: number;
    detail?: string;
}

export interface SageEmployeeIssues {
    employeeID: number;
    altEmployeeID: string;
    issues: SageReconciliationIssue[];
}

/**
 * One row Sage itself refused on a push, from the API's 422 { errorMessage, rejections } payload.
 * Distinct from SageReconciliationIssue: that one is our own pre-flight hour check, this one is
 * Sage talking back. `code` is the problem ("Invalid Department ID") and `value` is the offending
 * value ("2746") — Sage's own field names for these two are reversed.
 */
export interface SageApiRowRejection {
    linnum: number | null;
    column: string;
    code: string;
    value: string;
    workDate: string;
    payType: number | null;
    hours: string;
}

export interface SageEmployeeRejections {
    /** 0 when the API could not resolve the rejected line back to a row. */
    employeeID: number;
    altEmployeeID: string;
    rejections: SageApiRowRejection[];
}

/**
 * One employee the push refused because they were already exported this pay period
 * (the API's 409 { errorMessage, alreadyExported }). The facts are the employee's LATEST
 * prior export, so the force confirm can say exactly what is already sitting in Sage.
 */
export interface SageAlreadyExportedEmployee {
    employeeID: number;
    altEmployeeID: string;
    name?: string;
    /** ISO timestamp of the prior push. */
    exportedAt: string;
    exportedBy: string;
    rowCount: number;
    hours: number;
    /** false = Sage's answer was never received; the rows MAY be in Sage. */
    confirmed: boolean;
}

/** Thrown by both Sage calls so the pages can render the per-employee breakdown. */
export class SageExportError extends Error {
    issues?: SageEmployeeIssues[];
    rejections?: SageEmployeeRejections[];
    /** HTTP status when the failure came off the wire (409 / 422 / 502 …); undefined otherwise. */
    status?: number;
    /** 409 — the selection holds employees already exported this pay period; resend with force. */
    alreadyExported?: SageAlreadyExportedEmployee[];
    /**
     * 502 — Sage's answer was not received. The rows MAY be in Sage; the API recorded them as
     * unconfirmed (export `unconfirmedExportID`) so a plain re-push is blocked until forced.
     */
    unconfirmed?: boolean;
    unconfirmedExportID?: string;
    constructor(
        message: string,
        issues?: SageEmployeeIssues[],
        rejections?: SageEmployeeRejections[],
        extra?: {
            status?: number;
            alreadyExported?: SageAlreadyExportedEmployee[];
            unconfirmed?: boolean;
            unconfirmedExportID?: string;
        }
    ) {
        super(message);
        this.name = "SageExportError";
        this.issues = issues;
        this.rejections = rejections;
        this.status = extra?.status;
        this.alreadyExported = extra?.alreadyExported;
        this.unconfirmed = extra?.unconfirmed;
        this.unconfirmedExportID = extra?.unconfirmedExportID;
    }
}

/**
 * The API aborts the whole export with 422 { errorMessage, issues } when payroll hours
 * can't be reconciled. With responseType "blob" that body arrives as a Blob, so it has
 * to be read back off the wire before it can reach the UI. Never truncate the message —
 * it lists every affected employee/day.
 *
 * A push can also come back 422 { errorMessage, rejections } — that is Sage refusing
 * specific rows and saving none of them. Two separate keys on purpose: the shapes differ,
 * and the reconciliation renderer prints hour deltas that a Sage rejection has none of.
 */
async function decodeSageError(error: unknown): Promise<SageExportError> {
    const response = (error as { response?: { data?: unknown; status?: number } })?.response;
    const data = response?.data;
    const status = typeof response?.status === "number" ? response.status : undefined;
    let body: unknown = data;
    if (data instanceof Blob) {
        const text = await data.text().catch(() => "");
        try {
            body = JSON.parse(text);
        } catch {
            body = text;
        }
    }
    if (typeof body === "string" && body.trim()) return new SageExportError(body.trim(), undefined, undefined, { status });
    const obj = body as {
        errorMessage?: string;
        message?: string;
        issues?: SageEmployeeIssues[];
        rejections?: SageEmployeeRejections[];
        alreadyExported?: SageAlreadyExportedEmployee[];
        unconfirmed?: boolean;
        exportID?: string;
    } | null;
    const message =
        obj?.errorMessage ||
        obj?.message ||
        (error instanceof Error ? error.message : "Sage export failed");
    return new SageExportError(
        message,
        Array.isArray(obj?.issues) ? obj?.issues : undefined,
        Array.isArray(obj?.rejections) ? obj?.rejections : undefined,
        {
            status,
            alreadyExported: Array.isArray(obj?.alreadyExported) ? obj?.alreadyExported : undefined,
            unconfirmed: obj?.unconfirmed === true ? true : undefined,
            unconfirmedExportID: obj?.unconfirmed === true && typeof obj?.exportID === "string" ? obj.exportID : undefined,
        }
    );
}

export interface SagePushResult {
    rowCount: number;
    message: string;
    /** Absent on an API build older than the export record (TAS-180). */
    employeeCount?: number;
    exportID?: string;
    forcedEmployeeCount?: number;
    /** Set when the push was forced over employees already in Sage. */
    warning?: string;
}

/**
 * `force` re-sends employees already exported this pay period (otherwise the API answers
 * 409 with `alreadyExported`). It goes on the wire only when true, so `pushSageRows(id)`
 * — the v1 Supervisor List call — sends exactly what it always has.
 */
export async function pushSageRows(payPeriodID: number, employeeIDs?: number[], force?: boolean): Promise<SagePushResult> {
    const body: { employeeIDs?: number[]; force?: true } = {};
    if (employeeIDs?.length) body.employeeIDs = employeeIDs;
    if (force === true) body.force = true;
    try {
        const res = await client.post("/admin/2020ph/pushSageRows", Object.keys(body).length ? body : null, {
            params: { payPeriodID },
        });
        return res.data;
    } catch (error) {
        throw await decodeSageError(error);
    }
}

/** One API push to Sage this pay period (GET /admin/2020ph/sageExportStatus). */
export interface SageExportSummary {
    exportID: string;
    pushedAt: string;
    exportedBy: string;
    employeeCount: number;
    rowCount: number;
    forcedCount: number;
    confirmed: boolean;
    /** Written by the API's backfill script (rows recomputed later), not a live push. */
    backfilled?: boolean;
    /** Backfill only: employees recorded without rows because they could not be rebuilt. */
    rowsUnavailableCount?: number;
}

/** An employee's LATEST export this pay period. Only employees with any export are listed. */
export interface SageExportedInfo {
    employeeID: number;
    altEmployeeID: string;
    name: string;
    exportID: string;
    /** ISO timestamp. */
    exportedAt: string;
    exportedBy: string;
    rowCount: number;
    hours: number;
    /** Distinct exports this pay period; more than 1 means the employee went out again. */
    exportCount: number;
    /** false = Sage's answer was never received; the rows MAY be in Sage. */
    confirmed: boolean;
    /** The latest export carried no Sage rows for this employee ("Nothing to export"). */
    noRows: boolean;
    /** The latest export is a backfill record (rows recomputed later), not the original push. */
    backfilled?: boolean;
    /** Backfill: exported (guarded) but the rows could not be rebuilt; rowCount is 0. */
    rowsUnavailable?: boolean;
    /** Why, when `rowsUnavailable`. */
    rowsUnavailableReason?: string;
    stillApproved: boolean;
    changedSinceExport: boolean;
    changeReasons: SageExportChangeReason[];
}

export type SageExportChangeReason = "reapproved" | "notApproved";

export interface SageExportStatus {
    payPeriodID: number;
    exports: SageExportSummary[];
    employees: SageExportedInfo[];
}

export async function getSageExportStatus(payPeriodID: number): Promise<SageExportStatus> {
    const res = await client.get("/admin/2020ph/sageExportStatus", { params: { payPeriodID } });
    return res.data as SageExportStatus;
}

/** An employee the Sage export modal warns about: not Current (status 1) in Sage, or not in Sage. */
export interface SageInactiveEmployee {
    employeeID: number;
    altEmployeeID: string;
    /** null = not in Sage's employee list at all. */
    sageStatus: number | null;
    /** "Quit", "On Leave", "Terminated", "Not found in Sage", … */
    statusLabel: string;
    /** YYYY-MM-DD, Sage's inactive date when it has one. */
    inactiveDate: string | null;
}

export interface SageInactiveEmployeesResult {
    /** false = Sage could not be asked; the export is unaffected. */
    checked: boolean;
    message?: string;
    employees: SageInactiveEmployee[];
}

/** Advisory only — the caller treats a failure the same as `checked: false`. */
export async function getSageInactiveEmployees(employeeIDs: number[]): Promise<SageInactiveEmployeesResult> {
    const res = await client.get("/admin/2020ph/sageInactiveEmployees", {
        params: { employeeIDs: employeeIDs.join(",") },
    });
    return res.data as SageInactiveEmployeesResult;
}

export async function generateSageExport(payPeriodID: number, fileName: string, employeeIDs?: number[]): Promise<void> {
    let response;
    try {
        response = await client.get("/admin/2020ph/generateSageFile", {
            // employeeIDs travels as a comma-separated string — a raw array would serialize
            // as repeated params, which the endpoint doesn't accept.
            params: { payPeriodID, ...(employeeIDs?.length ? { employeeIDs: employeeIDs.join(",") } : {}) },
            responseType: "blob",
        });
    } catch (error) {
        throw await decodeSageError(error);
    }
    const url = URL.createObjectURL(response.data as Blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, 100);
}

export async function startSageReconciliation(payPeriodID: number): Promise<JobStartResponse> {
    try {
        const res = await client.post("/admin/2020ph/sageReconciliationJob", { payPeriodID });
        return res.data as JobStartResponse;
    } catch (error) {
        throw await decodeSageError(error);
    }
}

/**
 * Same poller the Vista job-based reports use — this reconciliation reads a whole pay period
 * out of both T&T and Sage, so it runs for minutes and the caller has to be able to abort it.
 */
export const pollSageReconciliation = (
    jobID: number,
    onProgress?: (status: JobStatus) => void,
    signal?: AbortSignal
): Promise<JobStatus> => pollVistaJob(jobID, onProgress, signal);

export async function downloadSageReconciliation(jobID: number, fileName: string): Promise<void> {
    let response;
    try {
        response = await client.get(`/admin/2020ph/sageReconciliationJob/${jobID}/download`, {
            responseType: "blob",
        });
    } catch (error) {
        throw await decodeSageError(error);
    }
    const disposition: string | undefined = response.headers["content-disposition"];
    let name = fileName;
    if (disposition) {
        const match = disposition.match(/filename[^;=\n]*=(['"]?)([^'";\n]+)\1/);
        if (match) name = match[2].trim();
    }
    const url = URL.createObjectURL(response.data as Blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, 100);
}

/** Start → poll → download. Throws SageExportError so the board can render it like any other Sage failure. */
export async function downloadSageReconciliationReport(
    payPeriodID: number,
    fileName: string,
    onProgress?: (status: JobStatus) => void,
    signal?: AbortSignal
): Promise<void> {
    const { jobID } = await startSageReconciliation(payPeriodID);
    const finalStatus = await pollSageReconciliation(jobID, onProgress, signal);
    if (finalStatus.status === JobStatusEnum.FAILED) {
        const resultData = finalStatus.resultData as { error?: string } | undefined;
        throw new SageExportError(resultData?.error ?? "Reconciliation failed");
    }
    await downloadSageReconciliation(jobID, fileName);
}
